import { useCallback, useEffect, useLayoutEffect, useRef } from 'react';
import styled from '@emotion/styled';
import { css } from '@emotion/react';
import { MDSTypography } from '@marqvision/mds-v2';

const Styled = {
  Wrapper: styled.div`
    position: relative;
    color: transparent;
    overflow-wrap: break-word;
    cursor: text;
  `,
  Textarea: styled(MDSTypography)<{ isDisabled?: boolean }>`
    width: 100%;
    padding: 4.5px 11px;
    &::placeholder {
      color: ${({ theme: { color } }) => color.content.placeholder.normal};
    }
    ${({ isDisabled, theme: { color } }) =>
      isDisabled &&
      css`
        color: ${color.content.neutral.tertiary.normal} !important;
        pointer-events: none;
        user-select: none;
        cursor: default;
      `}
  `,
};

type Props = {
  value: string;
  maxHeight: number;
  isDisabled?: boolean;
  onChange?: (value: string) => void;
};

export const Textarea = (props: Props) => {
  const { value, onChange, maxHeight, isDisabled } = props;

  const wrapperRef = useRef<HTMLDivElement>(null);

  const { handleKeyDown, handlePaste, handleDrop, createChangeHandler, createClickHandler, readonlyProps } =
    useReadonlyInput({ isDisabled });

  const handleWrapperClick = createClickHandler(() => {
    const textarea = wrapperRef.current?.querySelector('textarea');
    if (!textarea) return;
    textarea.focus();
  });

  const handleChange = createChangeHandler<HTMLTextAreaElement>((event) => {
    onChange?.(event.target.value);
  });

  useLayoutEffect(() => {
    // value 값 변경 시 사이즈 조정
    const textarea = wrapperRef.current?.querySelector('textarea');
    if (!textarea) return;
    const resize = () => {
      textarea.style.height = 'auto';
      textarea.style.height = Math.min(maxHeight, textarea.scrollHeight) + 'px';
    };
    resize();
  }, [value, maxHeight]);

  useEffect(() => {
    const wrapper = wrapperRef.current;
    if (!wrapper) return;

    const observer = new ResizeObserver(() => {
      requestAnimationFrame(() => {
        const textarea = wrapper.querySelector('textarea');
        if (!textarea) return;
        textarea.style.height = 'auto';
        textarea.style.height = Math.min(maxHeight, textarea.scrollHeight) + 'px';
      });
    });

    observer.observe(wrapper);
    return () => {
      observer.unobserve(wrapper);
    };
  }, [maxHeight]);

  return (
    <Styled.Wrapper ref={wrapperRef} onClick={handleWrapperClick}>
      <Styled.Textarea
        as="textarea"
        placeholder="Add comment"
        value={value}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        onPaste={handlePaste}
        onDrop={handleDrop}
        isDisabled={isDisabled}
        autoFocus={true}
        {...readonlyProps}
      />
    </Styled.Wrapper>
  );
};




const useReadonlyInput = ({ isDisabled }: { isDisabled?: boolean }) => {
  // 키보드 입력 방지
  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLTextAreaElement | HTMLInputElement>) => {
      if (isDisabled) {
        event.preventDefault();
        return false;
      }
    },
    [isDisabled]
  );

  // 붙여넣기 방지
  const handlePaste = useCallback(
    (event: React.ClipboardEvent<HTMLTextAreaElement | HTMLInputElement>) => {
      if (isDisabled) {
        event.preventDefault();
        return false;
      }
    },
    [isDisabled]
  );

  // 드래그 앤 드롭 방지
  const handleDrop = useCallback(
    (event: React.DragEvent<HTMLTextAreaElement | HTMLInputElement>) => {
      if (isDisabled) {
        event.preventDefault();
        return false;
      }
    },
    [isDisabled]
  );

  // 변경 이벤트 래퍼
  const createChangeHandler = useCallback(
    <T extends HTMLTextAreaElement | HTMLInputElement>(originalHandler?: (event: React.ChangeEvent<T>) => void) => {
      return (event: React.ChangeEvent<T>) => {
        if (isDisabled) return;
        originalHandler?.(event);
      };
    },
    [isDisabled]
  );

  // 클릭 이벤트 래퍼
  const createClickHandler = useCallback(
    (originalHandler?: () => void) => {
      return () => {
        if (isDisabled) return;
        originalHandler?.();
      };
    },
    [isDisabled]
  );

  return {
    handleKeyDown,
    handlePaste,
    handleDrop,
    createChangeHandler,
    createClickHandler,
    readonlyProps: {
      readOnly: isDisabled,
      tabIndex: isDisabled ? -1 : 0,
    },
  };
};
